import {
	Anchor,
	Divider,
	Group,
	Modal,
	ScrollArea,
	Stack,
	Table,
	useMantineTheme,
} from '@mantine/core';

import { useDisclosure } from '@mantine/hooks';
import { Text } from '@repo/foundations';
import type { FC } from 'react';
import { useMetricLatestFromWidget } from '../../../../api/hooks/metric/useMetric';
import { ScrollableModal } from '../../../../components/ScrollableModal/ScrollableModal';
import { WidgetType } from '../../../../interfaces';
import type { ISampleOrActualMetricWidget } from '../../constants';
import { METRIC_WIDGET_CONTENT_HEIGHT } from '../../constants';
import { formatMetricWidgetTitle } from '../../utils/utils';
import { MetricLoadingOrEmpty } from '../MetricLoadingOrEmpty';
import { ValueBadge } from '../ValueBadge';

export type TableComponentProps = {
	values: Array<Record<string, string | number>>;
	infoColumns: Array<{ fieldName: string; label: string }>;
	loadAll?: boolean;
};

type IMetricWidgetTableProps = {
	TableComponent: FC<TableComponentProps>;
	infoColumns: Array<{ fieldName: string; label: string }>;
} & ISampleOrActualMetricWidget;

const SEE_ALL_BUTTON_HEIGHT = 20;

export function MetricWidgetTable({
	source,
	metricWidget,
	TableComponent,
	infoColumns,
}: IMetricWidgetTableProps) {
	const theme = useMantineTheme();
	const [
		seeAllModalOpened,
		{ open: openSeeAllModal, close: closeSeeAllModal },
	] = useDisclosure(false);

	const { data: metric, isLoading } = useMetricLatestFromWidget(metricWidget);
	const value = metric?.value;

	const validTableMetric = (
		tableMetric: number | Array<Record<string, string | number>> | undefined
	): tableMetric is Array<Record<string, string | number>> =>
		tableMetric !== undefined && typeof tableMetric === 'object';

	// The metric ran successfully but produced no rows (e.g. no editor
	// activity in the lookback window). Show a dedicated empty state so the
	// user can distinguish this from "the metric has never been generated".
	const ranButEmpty = validTableMetric(value) && value.length === 0;

	if (!validTableMetric(value) || ranButEmpty || isLoading) {
		return (
			<MetricLoadingOrEmpty
				isLoading={isLoading}
				isEmpty={!validTableMetric(value)}
				hasNoEvents={ranButEmpty}
				source={source}
			/>
		);
	}

	if (source === 'preview') {
		const firstCols = infoColumns.slice(0, 2);
		return (
			<Table horizontalSpacing={2} verticalSpacing={2} fontSize="xs">
				<thead>
					<tr>
						{firstCols.map((col) => (
							<th key={col.fieldName}>
								<Text size="xxs">{col.label}</Text>
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					<tr>
						{firstCols.map((col) => (
							<td key={col.fieldName}>
								<Text size="xxs">...</Text>
							</td>
						))}
					</tr>
					<tr>
						{firstCols.map((col) => (
							<td key={col.fieldName}>
								<Text size="xxs">...</Text>
							</td>
						))}
					</tr>
				</tbody>
			</Table>
		);
	}

	let scrollAreaHeight = 0;

	if (source === 'widget') {
		scrollAreaHeight =
			METRIC_WIDGET_CONTENT_HEIGHT[source] - SEE_ALL_BUTTON_HEIGHT;
	} else {
		scrollAreaHeight = METRIC_WIDGET_CONTENT_HEIGHT[source] + 32;
	}

	return (
		<Stack spacing={0} w="100%">
			{metricWidget.type === WidgetType.AI_CHAT_HISTORY ? (
				<Modal
					size="xl"
					opened={seeAllModalOpened}
					onClose={closeSeeAllModal}
					title={formatMetricWidgetTitle(metricWidget)}
					styles={{
						content: {
							height: '100%',
						},
						body: {
							height: 'calc(100% - 53px)',
						},
					}}
				>
					<TableComponent values={value} infoColumns={infoColumns} loadAll />
				</Modal>
			) : (
				<ScrollableModal
					title={formatMetricWidgetTitle(metricWidget)}
					onClose={closeSeeAllModal}
					opened={seeAllModalOpened}
					size="xl"
				>
					<TableComponent values={value} infoColumns={infoColumns} loadAll />
				</ScrollableModal>
			)}
			{/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
			{/* @ts-ignore poorly typed component by Mantine */}
			<ScrollArea.Autosize
				h={scrollAreaHeight}
				sx={{ overflowX: 'auto', width: '100%' }}
			>
				<TableComponent values={value} infoColumns={infoColumns} />
			</ScrollArea.Autosize>

			{source === 'widget' && (
				<>
					<Divider />
					<Group position="apart" py="sm" px="md">
						<Anchor
							color={theme.other.getColor('text/emphasis/default')}
							fw={500}
							fz="sm"
							onClick={openSeeAllModal}
						>
							See all
						</Anchor>
						<ValueBadge metricWidget={metricWidget} />
					</Group>
				</>
			)}
		</Stack>
	);
}
